/**
 * useEarlyPayment
 * Hook para manejar los 3 tipos de pago anticipado.
 * Settlement usa un flujo de 2 pasos: quote → confirmación → ejecución.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks';
import {
  applyAnticipatedPayment,
  applyAdvancedPayment,
  applySettlementPayment,
  getSettlementQuote,
} from '@/services/credit-engine.service';
import { PARTNER_ID } from '@/constants';
import type {
  EarlyPaymentType,
  EarlyPaymentResponse,
  SettlementQuoteResponse,
} from '@/types/early-payment.types';

export function useEarlyPayment(creditId: string, directoryId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<EarlyPaymentType | null>(null);
  const [lastResult, setLastResult] = useState<EarlyPaymentResponse | null>(null);

  // Settlement quote state
  const [settlementQuote, setSettlementQuote] = useState<SettlementQuoteResponse | null>(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);

  // ─── Shared onSuccess ──────────────────────────────────────────────────────

  function handleSuccess(data: { data: EarlyPaymentResponse }, label: string) {
    const result = data.data;
    setLastResult(result);
    toast.success(result.message || `${label} aplicado exitosamente`);
    // Invalidate credit detail to refresh schedule and balance
    void queryClient.invalidateQueries({
      queryKey: ['backoffice', 'credits', creditId, directoryId],
    });
    // Also invalidate credits list (outstanding balance changed)
    void queryClient.invalidateQueries({
      queryKey: ['backoffice', 'credits'],
      exact: false,
    });
    // Portfolio activo (puede haber cambiado)
    void queryClient.invalidateQueries({
      queryKey: ['portfolio'],
      exact: false,
    });
  }

  function handleError(label: string) {
    toast.error(`Error al aplicar ${label}. Intenta de nuevo.`);
  }

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const anticipatedMutation = useMutation({
    mutationFn: (amount: number) =>
      applyAnticipatedPayment(PARTNER_ID, { credit_id: creditId, amount_mxn: amount }),
    onSuccess: (data) => handleSuccess(data, 'Abono a capital'),
    onError: () => handleError('abono a capital'),
  });

  const advancedMutation = useMutation({
    mutationFn: (amount: number) =>
      applyAdvancedPayment(PARTNER_ID, { credit_id: creditId, amount_mxn: amount }),
    onSuccess: (data) => handleSuccess(data, 'Pago adelantado'),
    onError: () => handleError('pago adelantado'),
  });

  const settlementMutation = useMutation({
    mutationFn: () =>
      applySettlementPayment(PARTNER_ID, { credit_id: creditId }),
    onSuccess: (data) => handleSuccess(data, 'Liquidacion'),
    onError: () => handleError('liquidacion'),
  });

  // ─── Modal actions ─────────────────────────────────────────────────────────

  async function openModal(type: EarlyPaymentType) {
    setPaymentType(type);
    setLastResult(null);
    setSettlementQuote(null);
    setIsModalOpen(true);

    // For settlement, fetch quote immediately
    if (type === 'SETTLEMENT') {
      setIsLoadingQuote(true);
      try {
        const response = await getSettlementQuote(PARTNER_ID, creditId);
        setSettlementQuote((response as any).data ?? response);
      } catch {
        toast.error('Error al obtener cotización de liquidación.');
      } finally {
        setIsLoadingQuote(false);
      }
    }
  }

  function closeModal() {
    setIsModalOpen(false);
    setPaymentType(null);
    setLastResult(null);
    setSettlementQuote(null);
  }

  function submitPayment(amount?: number) {
    if (!paymentType) return;

    switch (paymentType) {
      case 'ANTICIPATED':
        if (amount && amount > 0) anticipatedMutation.mutate(amount);
        break;
      case 'ADVANCED':
        if (amount && amount > 0) advancedMutation.mutate(amount);
        break;
      case 'SETTLEMENT':
        settlementMutation.mutate();
        break;
    }
  }

  // ─── Computed state ────────────────────────────────────────────────────────

  const isPending =
    anticipatedMutation.isPending ||
    advancedMutation.isPending ||
    settlementMutation.isPending;

  const isSuccess =
    anticipatedMutation.isSuccess ||
    advancedMutation.isSuccess ||
    settlementMutation.isSuccess;

  return {
    // Modal
    isModalOpen,
    paymentType,
    openModal,
    closeModal,

    // Action
    submitPayment,

    // State
    isPending,
    isSuccess,
    lastResult,

    // Settlement quote
    settlementQuote,
    isLoadingQuote,
  };
}
